/**
 * Simulated constant-product bonding curve with a phantom quote reserve.
 *
 * virtualQuote0 = PHANTOM_QUOTE (0.3 ETH)
 * virtualToken0 = CURVE_SUPPLY * (PHANTOM_QUOTE + GRADUATION_QUOTE) / GRADUATION_QUOTE
 * k = virtualQuote0 * virtualToken0
 *
 * Spot (pre-fee): price = virtualQuote / virtualToken
 *
 * Fees are always taken on the quote leg BEFORE reserves move.
 * Only netQuote updates virtualQuote / realQuoteRaised.
 *
 * Frontend simulation only.
 */

import {
  BPS_DENOM,
  CREATOR_SHARE_BPS,
  CURVE_SUPPLY,
  GRADUATION_QUOTE_WEI,
  LP_SUPPLY,
  PHANTOM_QUOTE_WEI,
  PLATFORM_SHARE_BPS,
  SNIPE_TAX_DURATION_MS,
  SNIPE_TAX_START_BPS,
  TOTAL_SUPPLY,
  TRADE_FEE_BPS,
  WAD,
} from '@/lib/launchpad/constants'
import { sameAddress } from '@/lib/launchpad/contracts'
import type {
  BuyResult,
  CreateLaunchInput,
  LaunchState,
  QuoteFeeBreakdown,
  SellResult,
} from '@/lib/launchpad/types'
import { bpsOf, mulDiv, weiToEthNumber, weiToEthString } from '@/lib/launchpad/wei'
import { optionalTokenSocials } from '@/lib/tokenSocials'

export function launchGraduationQuoteWei(state: Pick<LaunchState, 'graduationQuote'>): bigint {
  return state.graduationQuote && state.graduationQuote > 0n
    ? state.graduationQuote
    : GRADUATION_QUOTE_WEI
}

export function launchGraduationProgress(state: Pick<LaunchState, 'realQuoteRaised' | 'graduationQuote'>): number {
  const target = launchGraduationQuoteWei(state)
  if (target <= 0n)
    return 0
  const raised = state.realQuoteRaised < 0n ? 0n : state.realQuoteRaised
  const pct = Number((raised * 10000n) / target) / 100
  if (!Number.isFinite(pct) || pct < 0)
    return 0
  return Math.min(100, pct)
}

/** Display ETH amount for a launch or factory graduationQuote(). */
export function formatGraduationQuoteEth(quoteWei?: bigint, maxDecimals = 6): string {
  const target = quoteWei && quoteWei > 0n ? quoteWei : GRADUATION_QUOTE_WEI
  return weiToEthString(target, maxDecimals)
}

export function launchGraduationQuoteEth(
  state: Pick<LaunchState, 'graduationQuote'>,
  maxDecimals = 6,
): string {
  return formatGraduationQuoteEth(launchGraduationQuoteWei(state), maxDecimals)
}

/** Same 0.3 / 4.2 ratio the factory stores on each launch, including test-mode curves. */
export function phantomForGraduation(graduationQuote = GRADUATION_QUOTE_WEI): bigint {
  const target = graduationQuote > 0n ? graduationQuote : GRADUATION_QUOTE_WEI
  return mulDiv(target, PHANTOM_QUOTE_WEI, GRADUATION_QUOTE_WEI)
}

export function initialVirtualToken(graduationQuote = GRADUATION_QUOTE_WEI): bigint {
  const target = graduationQuote > 0n ? graduationQuote : GRADUATION_QUOTE_WEI
  const phantom = phantomForGraduation(target)
  return mulDiv(CURVE_SUPPLY, phantom + target, target)
}

export function initialK(graduationQuote = GRADUATION_QUOTE_WEI): bigint {
  return phantomForGraduation(graduationQuote) * initialVirtualToken(graduationQuote)
}

export function decayingBuyTaxBps(elapsedMs: number): number {
  if (elapsedMs <= 0)
    return SNIPE_TAX_START_BPS
  if (elapsedMs >= SNIPE_TAX_DURATION_MS)
    return 0
  const remaining = SNIPE_TAX_DURATION_MS - elapsedMs
  return Math.floor((SNIPE_TAX_START_BPS * remaining) / SNIPE_TAX_DURATION_MS)
}

const MAX_PRICE_POINTS = 128

function recordPricePoint(state: LaunchState, atMs = Date.now()): LaunchState {
  const point = {
    t: atMs,
    supply: weiToEthNumber(state.tokensSold),
    price: Number(spotPriceWei(state)) / 1e18,
    phase: state.status === 'graduated' ? 'pool' as const : 'curve' as const,
  }
  const previous = state.priceHistory ?? []
  const last = previous[previous.length - 1]
  if (last && last.price === point.price && last.supply === point.supply && last.phase === point.phase)
    return { ...state, priceHistory: previous }
  const stamped = last && last.t >= point.t ? { ...point, t: last.t + 1 } : point
  return { ...state, priceHistory: [...previous, stamped].slice(-MAX_PRICE_POINTS) }
}

export function createLaunchState(input: CreateLaunchInput): LaunchState {
  const graduationQuote = input.graduationQuote && input.graduationQuote > 0n
    ? input.graduationQuote
    : GRADUATION_QUOTE_WEI
  const phantomQuote = phantomForGraduation(graduationQuote)
  const virtualToken = initialVirtualToken(graduationQuote)
  const state: LaunchState = {
    id: input.id,
    name: input.name,
    ticker: input.ticker,
    imageUrl: input.imageUrl,
    socials: optionalTokenSocials(input.socials),
    creatorAddress: input.creatorAddress,
    contractAddress: input.contractAddress,
    tokenAddress: input.tokenAddress,
    shareSlug: input.shareSlug,
    createdAtMs: input.createdAtMs,
    lastTradeAtMs: input.createdAtMs,
    tradeFeeBps: TRADE_FEE_BPS,
    creatorShareBps: CREATOR_SHARE_BPS,
    platformShareBps: PLATFORM_SHARE_BPS,
    virtualQuote: phantomQuote,
    virtualToken,
    k: phantomQuote * virtualToken,
    realQuoteRaised: 0n,
    graduationQuote,
    tokensSold: 0n,
    creatorFeesAccrued: 0n,
    platformFeesAccrued: 0n,
    antiSnipeFeesAccrued: 0n,
    status: 'curve',
    priceHistory: [{
      t: input.createdAtMs,
      supply: 0,
      price: 0,
      phase: 'curve',
    }],
  }
  return state
}

function soldCurveTokens(state: LaunchState): bigint {
  const remaining = remainingCurveTokens(state)
  if (remaining >= CURVE_SUPPLY)
    return 0n
  const sold = CURVE_SUPPLY - remaining
  return sold < 0n ? 0n : sold
}

const CHART_SAMPLES = 128

function chartPriceFromReserves(quote: bigint, token: bigint): number {
  if (token <= 0n)
    return 0
  const price = Number(mulDiv(quote, WAD, token)) / 1e18
  if (!Number.isFinite(price) || price < 0)
    return 0
  return price
}

export function chartPointsForLaunch(state: LaunchState) {
  const origin = {
    t: state.createdAtMs,
    supply: 0,
    price: 0,
    phase: 'curve' as const,
  }
  const sold = soldCurveTokens(state)
  if (sold <= 0n)
    return [origin]

  // Walk the genesis constant-product path to the live sold amount.
  // Quadratic spacing packs more samples near the live spot so the visible
  // tail matches current reserves after a refresh. Local priceHistory is
  // browser-specific and must not change the chart.
  const startToken = initialVirtualToken(launchGraduationQuoteWei(state))
  const k = PHANTOM_QUOTE_WEI * startToken
  const steps = CHART_SAMPLES - 1
  const denom = BigInt(steps * steps)
  const points = [origin]
  for (let i = 1; i < CHART_SAMPLES; i++) {
    const soldAt = (sold * BigInt(2 * i * steps - i * i)) / denom
    if (soldAt <= 0n)
      continue
    const token = startToken > soldAt ? startToken - soldAt : 1n
    const quote = k / token
    const supply = Number(soldAt) / 1e18
    const next = {
      t: state.createdAtMs + i,
      supply: Number.isFinite(supply) && supply > 0 ? supply : 0,
      price: chartPriceFromReserves(quote, token),
      phase: 'curve' as const,
    }
    const last = points[points.length - 1]
    if (last && last.supply === next.supply && last.price === next.price)
      continue
    points.push(next)
  }

  let liveSupply = Number(sold) / 1e18
  if (!Number.isFinite(liveSupply) || liveSupply < 0)
    liveSupply = 0
  const graduatedNow = state.status === 'graduated'
  // The Uniswap v4 pool opens at a different (lower) FDV than the curve's
  // final tick -- Price/Mcap stats correctly reflect that via
  // spotPriceWei/marketCapWei (21 ETH FDV from the 4.2 ETH / 200M LP pool
  // open). The historical curve chart line must not appear to crash at
  // graduation, so the pool-phase marker keeps the curve's last tick price
  // and only flips the phase label to mark where trading moved to the pool.
  const lastSampledPrice = points[points.length - 1]?.price ?? 0
  const livePrice = graduatedNow
    ? lastSampledPrice
    : Number(spotPriceWei(state)) / 1e18
  const live = {
    t: state.createdAtMs + CHART_SAMPLES,
    supply: liveSupply,
    price: Number.isFinite(livePrice) && livePrice > 0 ? livePrice : 0,
    phase: graduatedNow ? 'pool' as const : 'curve' as const,
  }
  const last = points[points.length - 1]
  if (!last || last.supply !== live.supply || last.price !== live.price || last.phase !== live.phase)
    points.push(live)
  return points
}

export function remainingCurveTokens(state: LaunchState): bigint {
  // The live virtual-token reserve is the direct constant-product state that
  // moves on every buy/sell, so it is the source of truth for unsold supply.
  // `tokensSold` can drift out of sync on hydration; only fall back to it
  // when the reserve itself is unusable.
  const startToken = initialVirtualToken(launchGraduationQuoteWei(state))
  const liveToken = state.virtualToken < 0n ? 0n : state.virtualToken

  if (liveToken > 0n && liveToken <= startToken) {
    const soldFromReserves = startToken - liveToken
    return soldFromReserves >= CURVE_SUPPLY ? 0n : CURVE_SUPPLY - soldFromReserves
  }

  const soldReported = state.tokensSold < 0n ? 0n : state.tokensSold
  return soldReported >= CURVE_SUPPLY ? 0n : CURVE_SUPPLY - soldReported
}

export function isLaunchGraduated(state: LaunchState): boolean {
  return state.status === 'graduated'
    || state.realQuoteRaised >= launchGraduationQuoteWei(state)
    || remainingCurveTokens(state) <= 0n
}

/** Quote wei per 1 whole token (1e18 units), pre-fee. */
export function spotPriceWei(state: LaunchState): bigint {
  if (isLaunchGraduated(state)) {
    const quote = bondedPoolQuoteWei(state)
    const token = bondedPoolTokenWei(state)
    if (token <= 0n)
      return 0n
    return mulDiv(quote, WAD, token)
  }
  if (state.virtualToken <= 0n)
    return 0n
  return mulDiv(state.virtualQuote, WAD, state.virtualToken)
}

/**
 * Bonded market opens with the graduation quote and 200M LP tokens
 * (not leftover curve inventory, and not the phantom-backed last curve tick).
 * open_price = 4.2 / 200M; FDV = open_price * 1B = 21 ETH.
 */
function bondedPoolQuoteWei(state: LaunchState): bigint {
  const quote = launchGraduationQuoteWei(state)
  return quote > 0n ? quote : GRADUATION_QUOTE_WEI
}

function bondedPoolTokenWei(_state: LaunchState): bigint {
  return LP_SUPPLY
}

/**
 * Fully diluted market cap: live spot price times the full 1B token supply.
 *
 * Must always equal spotPriceWei(state) * TOTAL_SUPPLY / WAD so FDV stays
 * internally consistent with the displayed spot price in every state,
 * including an empty curve (priced off the 0.3 ETH phantom reserve — FDV is
 * 0.35 ETH, not $0) and a graduated pool (priced off the live Uniswap v4
 * pool, not a one-time snapshot at the instant it opened). This
 * intentionally ignores realQuoteRaised, tokensSold, and curve sale
 * inventory: those already drive spotPriceWei and must not be reapplied
 * here, or FDV silently decouples from the price shown next to it.
 */
export function marketCapWei(state: LaunchState): bigint {
  const price = spotPriceWei(state)
  if (price <= 0n)
    return 0n
  return mulDiv(price, TOTAL_SUPPLY, WAD)
}

export function quoteFees(
  quoteAmount: bigint,
  snipeBps: number,
): QuoteFeeBreakdown {
  if (quoteAmount <= 0n)
    throw new Error('Quote amount must be greater than 0')

  const tradeFee = bpsOf(quoteAmount, TRADE_FEE_BPS)
  const creatorCut = bpsOf(tradeFee, CREATOR_SHARE_BPS)
  const platformCut = tradeFee - creatorCut
  const snipeCut = bpsOf(quoteAmount, snipeBps)
  const fee = tradeFee + snipeCut
  if (fee >= quoteAmount)
    throw new Error('Fees consume the entire quote amount')
  return {
    tradeFee,
    creatorCut,
    platformCut,
    snipeCut,
    fee,
    netQuote: quoteAmount - fee,
  }
}

/** Uniswap-v2 amount-out: dy = dx * y / (x + dx). Rounding favors the curve. */
export function getAmountOut(amountIn: bigint, reserveIn: bigint, reserveOut: bigint): bigint {
  if (amountIn <= 0n)
    throw new Error('Amount in must be greater than 0')
  if (reserveIn <= 0n || reserveOut <= 0n)
    throw new Error('Reserves must be greater than 0')
  return (amountIn * reserveOut) / (reserveIn + amountIn)
}

/** Inverse of getAmountOut, rounded up so the buyer always receives at least amountOut. */
export function getAmountIn(amountOut: bigint, reserveIn: bigint, reserveOut: bigint): bigint {
  if (amountOut <= 0n)
    throw new Error('Amount out must be greater than 0')
  if (reserveIn <= 0n || amountOut >= reserveOut)
    throw new Error('Amount out must be less than the output reserve')
  const numerator = reserveIn * amountOut
  const denominator = reserveOut - amountOut
  const quot = numerator / denominator
  return numerator % denominator === 0n ? quot : quot + 1n
}

function grossQuoteForNet(netQuote: bigint, snipeBps: number): bigint {
  const keepBps = Number(BPS_DENOM) - TRADE_FEE_BPS - snipeBps
  if (keepBps <= 0)
    throw new Error('Fees consume the entire quote amount')
  let gross = (netQuote * BPS_DENOM + BigInt(keepBps) - 1n) / BigInt(keepBps)
  if (gross <= 0n)
    gross = 1n
  while (quoteFees(gross, snipeBps).netQuote < netQuote)
    gross += 1n
  return gross
}

/** Constant-product buy using net quote only (no fees). */
export function swapBuyNet(virtualQuote: bigint, virtualToken: bigint, _k: bigint, netQuote: bigint): { newVirtualQuote: bigint, newVirtualToken: bigint, tokensOut: bigint } {
  const tokensOut = getAmountOut(netQuote, virtualQuote, virtualToken)
  if (tokensOut <= 0n)
    throw new Error('Buy produced no tokens')
  return {
    newVirtualQuote: virtualQuote + netQuote,
    newVirtualToken: virtualToken - tokensOut,
    tokensOut,
  }
}

/** Constant-product sell using tokens in (no fees). Inverse of swapBuyNet. */
export function swapSellRaw(virtualQuote: bigint, virtualToken: bigint, _k: bigint, tokenIn: bigint): { newVirtualQuote: bigint, newVirtualToken: bigint, rawQuoteOut: bigint } {
  const rawQuoteOut = getAmountOut(tokenIn, virtualToken, virtualQuote)
  if (rawQuoteOut <= 0n)
    throw new Error('Sell produced no quote')
  return {
    newVirtualQuote: virtualQuote - rawQuoteOut,
    newVirtualToken: virtualToken + tokenIn,
    rawQuoteOut,
  }
}

export function claimCreatorFees(state: LaunchState, claimant: string): { state: LaunchState, amount: bigint } {
  if (!sameAddress(state.creatorAddress, claimant))
    throw new Error('Only the token creator can claim creator fees')
  if (state.creatorFeesAccrued <= 0n)
    throw new Error('No creator fees to claim')

  const amount = state.creatorFeesAccrued
  return {
    state: {
      ...state,
      creatorFeesAccrued: 0n,
    },
    amount,
  }
}

function applyFeeLedger(state: LaunchState, fees: QuoteFeeBreakdown): LaunchState {
  return {
    ...state,
    creatorFeesAccrued: state.creatorFeesAccrued + fees.creatorCut,
    platformFeesAccrued: state.platformFeesAccrued + fees.platformCut,
    antiSnipeFeesAccrued: state.antiSnipeFeesAccrued + fees.snipeCut,
  }
}

export function graduate(state: LaunchState): LaunchState {
  if (state.status === 'graduated')
    throw new Error('Launch is already graduated')
  const unsold = remainingCurveTokens(state)
  const tokenReserve = LP_SUPPLY + unsold
  const quoteReserve = state.realQuoteRaised
  if (quoteReserve <= 0n || tokenReserve <= 0n)
    throw new Error('Cannot seed a simulated pool with empty reserves')
  return {
    ...state,
    status: 'graduated',
    simPool: {
      quoteReserve,
      tokenReserve,
      k: quoteReserve * tokenReserve,
    },
  }
}

function maybeGraduate(state: LaunchState): { state: LaunchState, graduated: boolean } {
  if (state.status === 'curve' && state.realQuoteRaised >= launchGraduationQuoteWei(state)) {
    return { state: graduate(state), graduated: true }
  }
  return { state, graduated: false }
}

export function buyOnCurve(state: LaunchState, quoteIn: bigint, nowMs: number): { state: LaunchState, result: BuyResult } {
  if (state.status !== 'curve')
    throw new Error('Curve buy/sell is disabled after graduation')
  if (quoteIn <= 0n)
    throw new Error('Quote in must be greater than 0')

  const remaining = remainingCurveTokens(state)
  if (remaining <= 0n)
    throw new Error('No curve tokens remaining')

  const snipeBps = decayingBuyTaxBps(nowMs - state.createdAtMs)
  let fees = quoteFees(quoteIn, snipeBps)
  let quoteSpent = quoteIn
  let swap = swapBuyNet(state.virtualQuote, state.virtualToken, state.k, fees.netQuote)
  let tokensOut = swap.tokensOut

  if (tokensOut > remaining) {
    tokensOut = remaining
    const netNeeded = getAmountIn(tokensOut, state.virtualQuote, state.virtualToken)
    quoteSpent = grossQuoteForNet(netNeeded, snipeBps)
    fees = quoteFees(quoteSpent, snipeBps)
    const excessNet = fees.netQuote - netNeeded
    if (excessNet > 0n) {
      fees = {
        ...fees,
        platformCut: fees.platformCut + excessNet,
        fee: fees.fee + excessNet,
        netQuote: netNeeded,
      }
    }
    swap = {
      newVirtualQuote: state.virtualQuote + netNeeded,
      newVirtualToken: state.virtualToken - tokensOut,
      tokensOut,
    }
  }

  const netIntoCurve = swap.newVirtualQuote - state.virtualQuote
  let next: LaunchState = applyFeeLedger({
    ...state,
    virtualQuote: swap.newVirtualQuote,
    virtualToken: swap.newVirtualToken,
    realQuoteRaised: state.realQuoteRaised + netIntoCurve,
    tokensSold: state.tokensSold + tokensOut,
    lastTradeAtMs: nowMs,
  }, fees)

  const graduated = maybeGraduate(next)
  next = recordPricePoint(graduated.state, nowMs)

  return {
    state: next,
    result: {
      ...fees,
      tokensOut,
      quoteSpent,
      graduated: graduated.graduated,
    },
  }
}

export function sellOnCurve(state: LaunchState, tokenIn: bigint): { state: LaunchState, result: SellResult } {
  if (state.status !== 'curve')
    throw new Error('Curve buy/sell is disabled after graduation')
  if (tokenIn <= 0n)
    throw new Error('Token amount must be greater than 0')
  if (tokenIn > state.tokensSold)
    throw new Error('Cannot sell more tokens than the curve has sold')

  const swap = swapSellRaw(state.virtualQuote, state.virtualToken, state.k, tokenIn)
  const fees = quoteFees(swap.rawQuoteOut, 0)
  const raised = state.realQuoteRaised > swap.rawQuoteOut
    ? state.realQuoteRaised - swap.rawQuoteOut
    : 0n

  const next = applyFeeLedger({
    ...state,
    virtualQuote: swap.newVirtualQuote,
    virtualToken: swap.newVirtualToken,
    realQuoteRaised: raised,
    tokensSold: state.tokensSold - tokenIn,
    lastTradeAtMs: Date.now(),
  }, fees)

  return {
    state: recordPricePoint(next),
    result: {
      tokenIn,
      rawQuoteOut: swap.rawQuoteOut,
      tradeFee: fees.tradeFee,
      creatorCut: fees.creatorCut,
      platformCut: fees.platformCut,
      snipeCut: 0n,
      fee: fees.fee,
      userReceives: fees.netQuote,
    },
  }
}

export function buyOnPool(state: LaunchState, quoteIn: bigint): { state: LaunchState, result: BuyResult } {
  if (state.status !== 'graduated' || !state.simPool)
    throw new Error('Simulated pool is only available after graduation')
  if (quoteIn <= 0n)
    throw new Error('Quote in must be greater than 0')

  const fees = quoteFees(quoteIn, 0)
  const pool = state.simPool
  const swap = swapBuyNet(pool.quoteReserve, pool.tokenReserve, pool.k, fees.netQuote)
  const next = applyFeeLedger({
    ...state,
    simPool: {
      quoteReserve: swap.newVirtualQuote,
      tokenReserve: swap.newVirtualToken,
      k: pool.k,
    },
    lastTradeAtMs: Date.now(),
  }, fees)

  return {
    state: recordPricePoint(next),
    result: {
      ...fees,
      tokensOut: swap.tokensOut,
      quoteSpent: quoteIn,
      graduated: false,
    },
  }
}

export function sellOnPool(state: LaunchState, tokenIn: bigint): { state: LaunchState, result: SellResult } {
  if (state.status !== 'graduated' || !state.simPool)
    throw new Error('Simulated pool is only available after graduation')
  if (tokenIn <= 0n)
    throw new Error('Token amount must be greater than 0')
  if (tokenIn > state.simPool.tokenReserve)
    throw new Error('Not enough pool token reserve')

  const pool = state.simPool
  const swap = swapSellRaw(pool.quoteReserve, pool.tokenReserve, pool.k, tokenIn)
  const fees = quoteFees(swap.rawQuoteOut, 0)
  const next = applyFeeLedger({
    ...state,
    simPool: {
      quoteReserve: swap.newVirtualQuote,
      tokenReserve: swap.newVirtualToken,
      k: pool.k,
    },
    lastTradeAtMs: Date.now(),
  }, fees)

  return {
    state: recordPricePoint(next),
    result: {
      tokenIn,
      rawQuoteOut: swap.rawQuoteOut,
      tradeFee: fees.tradeFee,
      creatorCut: fees.creatorCut,
      platformCut: fees.platformCut,
      snipeCut: 0n,
      fee: fees.fee,
      userReceives: fees.netQuote,
    },
  }
}
